import React, { useState, useCallback, useEffect } from 'react';
import KPICard from '../../components/Dashboard/KPICard/KPICard';
import LineChart from '../../components/Dashboard/LineChart/LineChart';
import GroupedBarChart from '../../components/Dashboard/GroupedBarChart/GroupedBarChart';
import PieChart from '../../components/Dashboard/PieChart/PieChart';
import { fetchApi } from '../../../helpers/fetch';
import './Admin.scss';

const Overview = () => {
    const [dateRange, setDateRange] = useState('7days');
    const [loading, setLoading] = useState(false);
    
    // Default/initial data structure combining Revenue, Creators, and Users data
    const [overviewData, setOverviewData] = useState({
        // Revenue Section
        revenue: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        revenueTrend: [],
        gmv: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        ltv: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        avgGmvPerActiveUser: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        avgGmvPerActiveCreator: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        totalTransactions: 0,
        avgTransactionsPerCreator: 0,
        avgTimeToFirstPublication: '0h',
        avgTimeToFirstMonetisation: '0h',
        avgTimeToPurchase: '0h',
        avgTimeToFirstPurchase: '0h',
        purchaseFrequency: 0,
        avgTimeSpentPerUser: 0,
        
        // Creators and Users Section
        totalVisitors: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        totalUsers: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        totalCreators: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        totalMonetisedCreators: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        creatorsPaidVsFree: [],
        creatorsPaidVsFreePercentage: { value: 0, trend: null, trendLabel: 'since last week' },
        paidVsFreeCreations: [],
        totalCreations: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        avgCreationsPerCreator: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        totalNews: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        avgNewsPerCreator: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
        creatorsPerTier: [],
        potentialReach: { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null }
    });

    const formatCurrency = useCallback((amount) => {
        return `€${(amount / 100).toFixed(2)}`;
    }, []);

    // Helper function to extract trend data array
    const extractTrendData = useCallback((trendArray) => {
        if (!trendArray || !Array.isArray(trendArray) || trendArray.length === 0) {
            return undefined;
        }
        
        const trendData = trendArray.map(item => {
            if (typeof item === 'number') {
                return item;
            }
            if (item && typeof item === 'object') {
                return item.value !== undefined ? item.value : item;
            }
            return Number(item) || 0;
        }).filter(val => !isNaN(val) && val !== null && val !== undefined);
        
        return trendData.length > 0 ? trendData : undefined;
    }, []);

    // Helper function to extract value and trend from API response
    const extractValueAndTrend = useCallback((data) => {
        if (data && typeof data === 'object' && 'value' in data) {
            const trendValue = data.trend ? parseFloat(data.trend.replace(/[+%]/g, '')) : null;
            return {
                value: data.value,
                trend: trendValue,
                absoluteChange: data.absoluteChange !== undefined ? data.absoluteChange : null,
                trendLabel: data.trendLabel || 'since last week',
                trendData: data.trendData || null
            };
        }
        return {
            value: data || 0,
            trend: null,
            absoluteChange: null,
            trendLabel: 'since last week',
            trendData: null
        };
    }, []);

    const fetchOverviewData = useCallback(async () => {
        try {
            setLoading(true);
            
            const response = await fetchApi(`/admin/overview?period=${dateRange}`);
            
            console.log('/admin/overview response:', response);
            
            if (response.success && response.data) {
                // Ensure all data is properly structured
                const data = response.data;
                setOverviewData({
                    // Revenue Section
                    revenue: data.revenue || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    revenueTrend: data.revenueTrend || [],
                    gmv: data.gmv || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    ltv: data.ltv || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    avgGmvPerActiveUser: data.avgGmvPerActiveUser || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    avgGmvPerActiveCreator: data.avgGmvPerActiveCreator || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    totalTransactions: typeof data.totalTransactions === 'object' ? (data.totalTransactions?.value || 0) : (data.totalTransactions || 0),
                    avgTransactionsPerCreator: typeof data.avgTransactionsPerCreator === 'object' ? (data.avgTransactionsPerCreator?.value || 0) : (data.avgTransactionsPerCreator || 0),
                    avgTimeToFirstPublication: data.avgTimeToFirstPublication || '0h',
                    avgTimeToFirstMonetisation: data.avgTimeToFirstMonetisation || '0h',
                    avgTimeToPurchase: data.avgTimeToPurchase || '0h',
                    avgTimeToFirstPurchase: data.avgTimeToFirstPurchase || '0h',
                    purchaseFrequency: typeof data.purchaseFrequency === 'object' ? (data.purchaseFrequency?.value || 0) : (data.purchaseFrequency || 0),
                    avgTimeSpentPerUser: typeof data.avgTimeSpentPerUser === 'object' ? (data.avgTimeSpentPerUser?.value || 0) : (data.avgTimeSpentPerUser || 0),
                    
                    // Creators and Users Section
                    totalVisitors: data.totalVisitors || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    totalUsers: data.totalUsers || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    totalCreators: data.totalCreators || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    totalMonetisedCreators: data.totalMonetisedCreators || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    creatorsPaidVsFree: data.creatorsPaidVsFree || [],
                    creatorsPaidVsFreePercentage: data.creatorsPaidVsFreePercentage || { value: 0, trend: null, trendLabel: 'since last week' },
                    paidVsFreeCreations: data.paidVsFreeCreations || [],
                    totalCreations: data.totalCreations || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    avgCreationsPerCreator: data.avgCreationsPerCreator || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    totalNews: data.totalNews || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    avgNewsPerCreator: data.avgNewsPerCreator || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null },
                    creatorsPerTier: data.creatorsPerTier || [],
                    potentialReach: data.potentialReach || { value: 0, trend: null, trendLabel: 'since last week', trendData: null, absoluteChange: null }
                });
            } else {
                console.warn('API response not successful:', response);
            }
        } catch (error) {
            console.error('Error fetching overview data:', error);
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchOverviewData();
    }, [fetchOverviewData]);

    // Ensure all chart data is always an array
    const revenueTrendData = Array.isArray(overviewData.revenueTrend) ? overviewData.revenueTrend : [];
    const creatorsPaidVsFreeData = Array.isArray(overviewData.creatorsPaidVsFree) ? overviewData.creatorsPaidVsFree : [];
    const paidVsFreeCreationsData = Array.isArray(overviewData.paidVsFreeCreations) ? overviewData.paidVsFreeCreations : [];
    const creatorsPerTierData = Array.isArray(overviewData.creatorsPerTier) ? overviewData.creatorsPerTier : [];

    return (
        <div className="overview-dashboard">
            <div className="dashboard-header">
                <div className="header-controls">
                    <select 
                        value={dateRange} 
                        onChange={(e) => setDateRange(e.target.value)}
                        className="date-range-select"
                    >
                        <option value="7days">Last 7 days</option>
                        <option value="30days">Last 30 days</option>
                        <option value="90days">Last 90 days</option>
                        <option value="1year">Last year</option>
                    </select>
                    <button className="export-btn">Export</button>
                </div>
            </div>

            {/* Revenue and GMV Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Revenue and GMV</h2>
                </div>
                
                <div className="revenue-main">
                    <div className="revenue-kpi-chart">
                        {(() => {
                            const revenue = extractValueAndTrend(overviewData.revenue);
                            // Check if trendData is in the revenue object, otherwise fall back to revenueTrend array
                            const trendData = revenue.trendData 
                                ? extractTrendData(revenue.trendData) 
                                : extractTrendData(revenueTrendData);
                            // For LineChart, use the full trend data array (not just extracted values)
                            const chartData = revenue.trendData 
                                ? (Array.isArray(revenue.trendData) ? revenue.trendData : [])
                                : revenueTrendData;
                            return (
                                <>
                                    <KPICard
                                        title="Revenue"
                                        value={formatCurrency(revenue.value)}
                                        trend={revenue.trend}
                                        trendLabel={revenue.trendLabel}
                                        trendData={trendData}
                                        absoluteChange={revenue.absoluteChange}
                                        formatAbsoluteChange={formatCurrency}
                                        color={revenue.trend && revenue.trend >= 0 ? "#4caf50" : "#FF3232"}
                                        loading={loading}
                                    />
                                    <div className="chart-container">
                                        <LineChart
                                            data={Array.isArray(chartData) ? chartData : []}
                                            xAxisKey="date"
                                            lines={[{ key: 'value', color: '#FF3232', name: 'Revenue' }]}
                                            height={250}
                                            formatTooltip={formatCurrency}
                                            loading={loading}
                                        />
                                    </div>
                                </>
                            );
                        })()}
                    </div>

                    <div className="revenue-kpis-grid">
                    {(() => {
                            const gmv = extractValueAndTrend(overviewData.gmv);
                            const trendData = gmv.trendData ? extractTrendData(gmv.trendData) : undefined;
                        return (
                            <KPICard
                                    title="GMV"
                                    value={formatCurrency(gmv.value)}
                                    trend={gmv.trend}
                                    trendLabel={gmv.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={gmv.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={gmv.trend && gmv.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                            const ltv = extractValueAndTrend(overviewData.ltv);
                            const trendData = ltv.trendData ? extractTrendData(ltv.trendData) : undefined;
                            return (
                        <KPICard
                                    title="LTV"
                                    value={formatCurrency(ltv.value)}
                                    trend={ltv.trend}
                                    trendLabel={ltv.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={ltv.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={ltv.trend && ltv.trend >= 0 ? "#4caf50" : "#f44336"}
                            loading={loading}
                        />
                            );
                        })()}
                    {(() => {
                            const avgGmvUser = extractValueAndTrend(overviewData.avgGmvPerActiveUser);
                            const trendData = avgGmvUser.trendData ? extractTrendData(avgGmvUser.trendData) : undefined;
                        return (
                            <KPICard
                                    title="Avg. GMV per active user"
                                    value={formatCurrency(avgGmvUser.value)}
                                    trend={avgGmvUser.trend}
                                    trendLabel={avgGmvUser.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={avgGmvUser.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={avgGmvUser.trend && avgGmvUser.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                            const avgGmvCreator = extractValueAndTrend(overviewData.avgGmvPerActiveCreator);
                            const trendData = avgGmvCreator.trendData ? extractTrendData(avgGmvCreator.trendData) : undefined;
                        return (
                            <KPICard
                                    title="Avg. GMV per active creator"
                                    value={formatCurrency(avgGmvCreator.value)}
                                    trend={avgGmvCreator.trend}
                                    trendLabel={avgGmvCreator.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={avgGmvCreator.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={avgGmvCreator.trend && avgGmvCreator.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    </div>
                </div>

                <div className="overview-kpis-grid">
                    <KPICard
                        title="Total number of transactions"
                        value={overviewData.totalTransactions || 0}
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. transactions per creator"
                        value={overviewData.avgTransactionsPerCreator || 0}
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time to first publication"
                        value={overviewData.avgTimeToFirstPublication || '0h'}
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time to first monetisation"
                        value={overviewData.avgTimeToFirstMonetisation || '0h'}
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time to purchase"
                        value={overviewData.avgTimeToPurchase || '0h'}
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time to first purchase"
                        value={overviewData.avgTimeToFirstPurchase || '0h'}
                        loading={loading}
                    />
                    <KPICard
                        title="Purchase frequency"
                        value={overviewData.purchaseFrequency || 0}
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time spent per user"
                        value={overviewData.avgTimeSpentPerUser ? `${Math.round(overviewData.avgTimeSpentPerUser)}mins` : '0mins'}
                        loading={loading}
                    />
                </div>
            </div>

            {/* Creators and Users Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Creators and Users</h2>
                </div>

                <div className="creators-users-kpis-grid">
                    {(() => {
                        const totalVisitors = extractValueAndTrend(overviewData.totalVisitors);
                        const trendData = totalVisitors.trendData ? extractTrendData(totalVisitors.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Visitors"
                                value={totalVisitors.value}
                                trend={totalVisitors.trend}
                                trendLabel={totalVisitors.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalVisitors.absoluteChange}
                                color={totalVisitors.trend && totalVisitors.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const totalUsers = extractValueAndTrend(overviewData.totalUsers);
                        const trendData = totalUsers.trendData ? extractTrendData(totalUsers.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Users"
                                value={totalUsers.value}
                                trend={totalUsers.trend}
                                trendLabel={totalUsers.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalUsers.absoluteChange}
                                color={totalUsers.trend && totalUsers.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const totalCreators = extractValueAndTrend(overviewData.totalCreators);
                        const trendData = totalCreators.trendData ? extractTrendData(totalCreators.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Creators"
                                value={totalCreators.value}
                                trend={totalCreators.trend}
                                trendLabel={totalCreators.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalCreators.absoluteChange}
                                color={totalCreators.trend && totalCreators.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const totalMonetised = extractValueAndTrend(overviewData.totalMonetisedCreators);
                        const trendData = totalMonetised.trendData ? extractTrendData(totalMonetised.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Monetised Creators"
                                value={totalMonetised.value}
                                trend={totalMonetised.trend}
                                trendLabel={totalMonetised.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalMonetised.absoluteChange}
                                color={totalMonetised.trend && totalMonetised.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                </div>

                <div className="creators-charts-row">
                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">Creators (Paid vs Free Creations)</span>
                            {(() => {
                                const paidVsFree = extractValueAndTrend(overviewData.creatorsPaidVsFreePercentage);
                                return (
                                    <>
                                        <span className="kpi-value">{paidVsFree.value}%</span>
                                        {paidVsFree.trend !== null && (
                                            <span className={`kpi-trend ${paidVsFree.trend >= 0 ? 'positive' : 'negative'}`}>
                                                {paidVsFree.trend >= 0 ? '+' : ''}{paidVsFree.trend?.toFixed(1)}% {paidVsFree.trendLabel}
                                            </span>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        <GroupedBarChart
                            data={creatorsPaidVsFreeData}
                            xAxisKey="date"
                            bars={[
                                { key: 'Paid', color: '#FF8CDC', name: 'Creators with paid creations' },
                                { key: 'Free', color: '#D3F2FF', name: 'Creators with free creations only' }
                            ]}
                            height={300}
                            formatTooltip={(value) => `${value}%`}
                            loading={loading}
                        />
                    </div>

                    <div className="chart-card">
                    <div className="chart-kpi">
                            <span className="kpi-label">Paid creations vs Free creations</span>
                    </div>
                        <PieChart
                            data={paidVsFreeCreationsData}
                            dataKey="value"
                            nameKey="name"
                            height={300}
                            colors={['#FF8CDC', '#8CDCFF']}
                            paddingAngle={5}
                            loading={loading}
                        />
                    </div>
                </div>

                <div className="creations-news-kpis-grid">
                    {(() => {
                        const totalCreations = extractValueAndTrend(overviewData.totalCreations);
                        const trendData = totalCreations.trendData ? extractTrendData(totalCreations.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Creations"
                                value={totalCreations.value}
                                trend={totalCreations.trend}
                                trendLabel={totalCreations.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalCreations.absoluteChange}
                                color={totalCreations.trend && totalCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const avgCreations = extractValueAndTrend(overviewData.avgCreationsPerCreator);
                        const trendData = avgCreations.trendData ? extractTrendData(avgCreations.trendData) : undefined;
                        return (
                            <KPICard
                                title="Avg. Creations per creator"
                                value={avgCreations.value}
                                trend={avgCreations.trend}
                                trendLabel={avgCreations.trendLabel}
                                trendData={trendData}
                                absoluteChange={avgCreations.absoluteChange}
                                color={avgCreations.trend && avgCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const totalNews = extractValueAndTrend(overviewData.totalNews);
                        const trendData = totalNews.trendData ? extractTrendData(totalNews.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total News"
                                value={totalNews.value}
                                trend={totalNews.trend}
                                trendLabel={totalNews.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalNews.absoluteChange}
                                color={totalNews.trend && totalNews.trend >= 0 ? "#3BB273" : "#C9100E"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const avgNews = extractValueAndTrend(overviewData.avgNewsPerCreator);
                        const trendData = avgNews.trendData ? extractTrendData(avgNews.trendData) : undefined;
                        return (
                        <KPICard
                                title="Avg. News per creator"
                                value={avgNews.value}
                                trend={avgNews.trend}
                                trendLabel={avgNews.trendLabel}
                                trendData={trendData}
                                absoluteChange={avgNews.absoluteChange}
                                color={avgNews.trend && avgNews.trend >= 0 ? "#4caf50" : "#f44336"}
                            loading={loading}
                        />
                        );
                    })()}
                </div>
              
                <div className="tier-section">
                    <div className="chart-card tier-chart">
                        <div className="chart-kpi">
                            <span className="kpi-label">Creators per tier (following)</span>
                        </div>
                        <PieChart
                            data={creatorsPerTierData}
                            dataKey="value"
                            nameKey="name"
                            height={300}
                            colors={['#FF8CDC', '#8CDCFF', '#FF3232', '#D3F2FF']}
                            loading={loading}
                        />
                    </div>

                    <div className="tier-kpis">
                        {(() => {
                            const potentialReach = extractValueAndTrend(overviewData.potentialReach);
                            const trendData = potentialReach.trendData ? extractTrendData(potentialReach.trendData) : undefined;
                            return (
                                <KPICard
                                    title="Potential Reach"
                                    value={potentialReach.value ? potentialReach.value.toLocaleString() : 0}
                                    trend={potentialReach.trend}
                                    trendLabel={potentialReach.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={potentialReach.absoluteChange}
                                    color={potentialReach.trend && potentialReach.trend >= 0 ? "#4caf50" : "#f44336"}
                                    loading={loading}
                                />
                            );
                        })()}
                        {(() => {
                            const avgCreations = extractValueAndTrend(overviewData.avgCreationsPerCreator);
                            const trendData = avgCreations.trendData ? extractTrendData(avgCreations.trendData) : undefined;
                            return (
                                <KPICard
                                    title="Avg. Creations per creator"
                                    value={avgCreations.value}
                                    trend={avgCreations.trend}
                                    trendLabel={avgCreations.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={avgCreations.absoluteChange}
                                    color={avgCreations.trend && avgCreations.trend >= 0 ? "#4caf50" : "#f44336"}
                                    loading={loading}
                                />
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Overview;

